import { z } from 'zod';
import { IsoDateTime, Uuid, patchOf } from './common';

const Text = (max = 200) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => (v === '' ? null : v));

/** Mod-11 check digit of the first `weights.length` digits. */
function checkDigit(body: string, weights: number[]): number {
  const sum = weights.reduce((n, w, i) => n + Number(body[i]) * w, 0);
  const r = sum % 11;
  return r < 2 ? 0 : 11 - r;
}
/** CPF weights: n+1 down to 2. */
const cpfWeights = (n: number) => Array.from({ length: n }, (_, i) => n + 1 - i);

/** CPF (11 digits) or CNPJ (14 digits) with valid check digits. */
export function validTaxId(value: string): boolean {
  const d = value.replace(/\D/g, '');
  if (!/^(\d{11}|\d{14})$/.test(d) || /^(\d)\1+$/.test(d)) return false;
  const digit = checkDigit;
  if (d.length === 11) {
    const w = cpfWeights;
    return digit(d, w(9)) === Number(d[9]) && digit(d, w(10)) === Number(d[10]);
  }
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, ...w1];
  return digit(d, w1) === Number(d[12]) && digit(d, w2) === Number(d[13]);
}

/** "12345678909" → "123.456.789-09"; CNPJ → "12.345.678/0001-95". */
export function formatTaxId(value: string): string {
  const d = value.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return value;
}

export const CustomerFields = z.object({
  name: z.string().trim().min(1, 'Informe o nome ou um apelido.').max(120),
  whatsapp: Text(30).default(null),
  email: z
    .string()
    .trim()
    .max(200)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v))
    .pipe(z.email().nullable()),
  marketplaceNickname: Text(80).default(null).describe('Apelido no marketplace.'),
  preferredChannelId: Uuid.nullable().default(null),
  notes: Text(2000).default(null),
  externalRef: Text().default(null),
  taxId: Text(20)
    .refine((v) => v === null || validTaxId(v), 'CPF ou CNPJ inválido.')
    .transform((v) => (v === null ? null : formatTaxId(v)))
    .default(null)
    .describe('CPF or CNPJ, only for an NF or direct shipping. Not in lists or CSV exports.'),
  address: Text(300)
    .default(null)
    .describe('"Endereço com CEP", one line. Not in lists or CSV exports.'),
});
export const CreateCustomerBody = CustomerFields.meta({
  example: { name: 'Mariana Souza', whatsapp: '(48) 99999-0040', preferredChannelId: null },
});
export type CreateCustomerBody = z.input<typeof CreateCustomerBody>;
export const UpdateCustomerBody = patchOf(CustomerFields);
export type UpdateCustomerBody = z.input<typeof UpdateCustomerBody>;

export const Customer = z
  .object({
    id: Uuid,
    name: z.string(),
    whatsapp: z.string().nullable(),
    email: z.string().nullable(),
    marketplaceNickname: z.string().nullable(),
    preferredChannel: z.object({ id: Uuid, name: z.string() }).nullable(),
    notes: z.string().nullable(),
    externalRef: z.string().nullable(),
    stats: z.object({
      quotes: z.number().int(),
      openQuotes: z.number().int().describe('Draft or sent.'),
      lastQuoteAt: IsoDateTime.nullable(),
      orders: z.number().int().describe('Orders not cancelled.'),
      spentCentavos: z
        .number()
        .int()
        .describe('"Total gasto": orders not cancelled, less refunds of returned ones.'),
      lastPurchaseOn: z.iso.date().nullable().describe('Sale date of the latest order.'),
    }),
    anonymizedAt: IsoDateTime.nullable(),
    anonymizedBy: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Customer' });
export type Customer = z.infer<typeof Customer>;
export const CustomerDetail = Customer.extend({
  taxId: z.string().nullable(),
  address: z.string().nullable(),
}).meta({ id: 'CustomerDetail' });
export type CustomerDetail = z.infer<typeof CustomerDetail>;

export const CustomerHistory = z
  .object({
    spentCentavos: z.number().int(),
    orders: z.number().int(),
    data: z.array(
      z.object({
        kind: z.enum(['quote', 'order']),
        id: Uuid,
        number: z.string().describe('"Q-2026-0041 v2" or "P-2026-0124".'),
        description: z.string(),
        status: z.string().describe('Quote or order status (a sent quote past validity: expired).'),
        valueCentavos: z.number().int(),
        at: IsoDateTime,
      }),
    ),
  })
  .meta({ id: 'CustomerHistory' });
export type CustomerHistory = z.infer<typeof CustomerHistory>;

export const DuplicateQuery = z.object({
  name: z.string().trim().max(120).optional(),
  whatsapp: z.string().trim().max(30).optional(),
});
export const CustomerDuplicates = z.object({ data: z.array(Customer) });
export const CustomerList = z.object({
  data: z.array(Customer),
  nextCursor: z.string().nullable(),
});
export const CustomerQuery = z.object({
  q: z
    .string()
    .trim()
    .max(100)
    .optional()
    .describe('Search name, WhatsApp, e-mail or marketplace nickname.'),
});
